import { computed, markRaw, nextTick, ref, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { defineStore, storeToRefs } from 'pinia';
import {
  createForm,
  createGeoTiffMapLayerGroup,
  createVectorMapLayerGroup,
  defaultStatisticsMapInfoValueFn,
  defaultZoneStyleFn,
  FormElement,
  getGeoTiffImage,
  integerValueUnitZoneLabelFn,
  MAP_PROJECTION_3857,
  MAP_PROJECTION_4326,
  mergeExtents,
  SelectableFormElement,
  setSelectableFormElementChildItems,
  transformExtent,
  useFormElement,
  useModals,
  type GeoTiffMapLayerConfig,
  type VectorMapLayerConfig
} from '@common';
import { useNotifications } from '@common';
import { LayerGroupClassification, Permission, useAuthStore, type LayerGroupBand, type WizardStep } from '@shared';
import { useApi } from '../../../services';
import type {
  ComparativeStatisticsParcel,
  ComparativeStatisticsParcelMetadata,
  SoilTemperaturePeriodValues,
  SoilTemperatureStatisticsParcel,
  SoilTemperatureStatisticsParcelMetadata,
  Statistics,
  StatisticsParcel,
  StatisticsParcelMetadata,
  StatisticsWizardData,
  StatisticsWizardListItem,
  StatisticsWizardParcelData,
  StatisticsWizardParcelItem
} from '../models';
import ParcelGroupModal from '../../parcel/components/parcel-group-modal/ParcelGroupModal.vue';
import type { ParcelGroupModalConfig } from '../../parcel/models/parcel-group-modal.model';
import 'nerdamer/Algebra.js';
import 'nerdamer/Calculus.js';
import 'nerdamer/Solve.js';
import { useModulesConfigStore } from '../../../store/modules-config.store';
import { getDemoOffer } from '../../parcel/utils/offer.utils';
import { StatisticsKind, StatisticsSource, StatisticsWizardParcelDataState } from '../enums';
import { LayerGroupKind } from '../../geo-layers-upload/enums';
import { SourceStep, StartStep } from '../components';
import OverviewStep from '../components/statistics-wizard/OverviewStep.vue';
import type { Extent } from 'ol/extent';
import { useColorMapStore } from '../../colormap';
import type { GeoTIFFImage } from 'ol/source/GeoTIFF';
import { parcelYearMapInfoSubTitleFn } from '../../map/utils/map.utils';
import { getStatisticsWizardParcelFormConfig } from '../config/statistics-parcel-form.config';
import { fieldHasMultipleValues, statisticsKindFieldNameMap } from '../utils';
import { useI18n } from 'vue-i18n';
import { useStatisticsWizardMap } from '../composables';
import { useInfoModal } from '../../../composables';

const MODULE_PERMISSION = Permission.STATISTICS;

export const useStatisticsWizardStore = defineStore('statisticsWizardStore', () => {
  const router = useRouter();
  const modals = useModals();
  const notifications = useNotifications();

  const parcelApi = useApi().parcel;
  const statisticsApi = useApi().statistics;

  const colorMapStore = useColorMapStore();
  const moduleConfigStore = useModulesConfigStore();
  const authStore = useAuthStore();

  const { moduleConfig } = storeToRefs(moduleConfigStore);
  const { isDemoOfferVisible } = storeToRefs(authStore);

  const { t } = useI18n();

  const { openInfoModal: openStartInfoModal } = useInfoModal('', 'statistics.label.info');
  const openInfoModal = () => openStartInfoModal('maximap.statistics.startInfo');

  const statisticsSourceView: Ref<StatisticsSource | undefined> = ref(StatisticsSource.YIELD);

  const statisticsData: Ref<StatisticsWizardData> = ref({
    name: undefined,
    year: undefined,
    kind: undefined,
    parcels: [],
    metadata: {
      parcels: []
    },
    status: undefined
  });

  const { openInfoModal: openSourceInfoModal } = useInfoModal('', 'statistics.label.wizardInfoTitle');

  const needMetadataUpdate = ref(true);

  const allParcelsValid = computed(() => {
    return statisticsData.value.parcels.every((parcel) => parcel.form.valid && !parcel.blocked);
  });

  const showStatisticsTypeChooser = computed(() => moduleConfig.value.statistics.statisticsTypeOptions.length > 1);

  const parcelsLoading = ref(false);

  const composedStatisticsWizardMap = useStatisticsWizardMap();
  const {
    map,
    isZoneScrollIntoViewDisabled,
    addLayer,
    clearLayers,
    removeLayer,
    zoomToExtent,
    switchSubLayer,
    updateMapInfos
  } = composedStatisticsWizardMap;

  const steps = ref<WizardStep[]>([
    {
      title: 'statistics.wizard.step.start',
      component: markRaw(StartStep),
      valid: () => !!statisticsData.value.kind && !!statisticsData.value.year
    },
    {
      title: 'statistics.wizard.step.source',
      component: markRaw(SourceStep),
      valid: () => validateSourceStep(),
      onLoad: () => onSourceStepLoad()
    },
    {
      title: 'statistics.wizard.step.overview',
      component: markRaw(OverviewStep)
    }
  ]);
  const currentStep = ref(1);
  const isFirstStep = computed(() => currentStep.value === 1);
  const isLastStep = computed(() => currentStep.value === steps.value.length);
  const isCurrentStepValid = computed(() => {
    const current = steps.value[currentStep.value - 1];
    if (typeof current.valid === 'function') {
      return current.valid();
    }
    return true;
  });

  const isNextStepLoading = ref(false);
  const isParcelsLoading = ref(false);
  const isLayerLoading = ref(false);
  const isSaving = ref(false);
  const isStatisticsSaveValid = ref(false);
  const isUiDisabled = computed(
    () => isNextStepLoading.value || isParcelsLoading.value || isSaving.value || isLayerLoading.value
  );

  const applySoilTemperaturePeriod = async (applySoilTemperaturePeriod: SoilTemperaturePeriodValues) => {
    statisticsData.value.parcels.forEach(async (parcel) => {
      const { getChild, validate } = useFormElement(parcel.form.element);

      const fromDateField = getChild('fromDate') as FormElement;
      const toDateField = getChild('toDate') as FormElement;

      const { value: fromDateValue } = useFormElement(fromDateField);
      const { value: toDateValue } = useFormElement(toDateField);

      if (applySoilTemperaturePeriod.fromDate) {
        fromDateValue.value = applySoilTemperaturePeriod.fromDate;
      }
      if (applySoilTemperaturePeriod.toDate) {
        toDateValue.value = applySoilTemperaturePeriod.toDate;
      }

      await validate();
    });
  };

  const copyStatistics = async (statisticsId: string) => {
    currentStep.value = 2;

    const originalStatisticsData = await loadStatisticsById(statisticsId);

    statisticsData.value = {
      name: undefined,
      year: originalStatisticsData.year,
      kind: originalStatisticsData.kind,
      parcels: [],
      metadata: {
        parcels: [
          ...originalStatisticsData.metadata.parcels.map((p) =>
            originalStatisticsData.kind === StatisticsKind.SOIL_TEMPERATURE
              ? ({ ...p, parcelId: p.id } as SoilTemperatureStatisticsParcelMetadata)
              : ({ ...p, parcelId: p.parcelId } as ComparativeStatisticsParcelMetadata)
          )
        ]
      }
    };

    const statisticsWizardParcelItems: StatisticsWizardParcelItem[] = originalStatisticsData.parcels.map(
      (parcel: StatisticsWizardParcelItem) => ({
        id: parcel.id,
        name: parcel.name,
        area: parcel.area,
        bbox: parcel.bbox,
        blocked: parcel.blocked,
        svg: parcel.svg,
        currentCrop: parcel.currentCrop || parcel.crop
      })
    );

    await copyStatisticsParcels(statisticsWizardParcelItems);
  };

  const copyStatisticsParcels = async (parcelIds: StatisticsWizardParcelItem[]) => {
    for (const parcel of parcelIds) {
      await setupSelectedParcel(parcel, true);
    }

    for (const parcel of statisticsData.value.parcels) {
      await setParcelFormItems(parcel, true);
      parcel.isLoading = false;
    }
  };

  const addParcelsMetadata = () => {
    statisticsData.value.metadata.parcels = [];

    if (statisticsData.value.kind === StatisticsKind.SOIL_TEMPERATURE) {
      for (const parcel of statisticsData.value.parcels) {
        addSoilTemperatureStatisticsMetadata(parcel);
      }
    } else {
      for (const parcel of statisticsData.value.parcels) {
        addComparativeStatisticsMetadata(parcel);
      }
    }
  };

  const addSoilTemperatureStatisticsMetadata = (parcel: StatisticsWizardParcelData) => {
    const { getChild } = useFormElement(parcel.form.element);

    if (statisticsData.value.kind === StatisticsKind.SOIL_TEMPERATURE) {
      const fromDateField = getChild('fromDate') as FormElement;
      const toDateField = getChild('toDate') as FormElement;
      const { value: fromDateValue } = useFormElement(fromDateField);
      const { value: toDateValue } = useFormElement(toDateField);

      const metadataParcel: SoilTemperatureStatisticsParcelMetadata = {
        id: parcel.id,
        parcelId: parcel.id,
        dateFrom: fromDateValue.value,
        dateTo: toDateValue.value,
        layerGroupKind: LayerGroupKind.SOIL_TEMPERATURE,
        statisticsSource: StatisticsSource.SOIL_TEMPERATURE
      };

      statisticsData.value.metadata.parcels.push(metadataParcel);
    }
  };

  const addComparativeStatisticsMetadata = (parcel: StatisticsWizardParcelData) => {
    if (statisticsData.value.kind) {
      const { getChild } = useFormElement(parcel.form.element);

      const yieldField = getChild('yield') as SelectableFormElement;
      const { value: yieldValue } = useFormElement(yieldField);
      addComparativeStatisticsParcelMetadata(parcel.id, yieldValue.value);

      const field = getChild(statisticsKindFieldNameMap[statisticsData.value.kind]!) as FormElement;
      const { value: fieldValue } = useFormElement(field);

      if (Array.isArray(fieldValue.value)) {
        fieldValue.value.forEach((value) => {
          addComparativeStatisticsParcelMetadata(parcel.id, value);
        });
      } else {
        addComparativeStatisticsParcelMetadata(parcel.id, fieldValue.value);
      }
    }
  };

  const addComparativeStatisticsParcelMetadata = (parcelId: string, fieldValue: any) => {
    statisticsData.value.metadata.parcels.push({
      id: parcelId,
      parcelId: parcelId,
      layerGroupId: fieldValue.layerGroupId,
      layerGroupKind: fieldValue.layerGroupKind,
      statisticsSource: fieldValue.statisticsSource,
      label: fieldValue.name,
      value: fieldValue.value,
      year: fieldValue.year,
      raster: fieldValue.raster
    });
  };

  const getParcelMetadataByParcelId = async (parcelId: string): Promise<StatisticsParcel | undefined> => {
    const parcels = statisticsData.value.metadata.parcels;

    if (!parcels) return;

    return statisticsData.value.kind === StatisticsKind.SOIL_TEMPERATURE
      ? parcels.find((p: StatisticsParcelMetadata) => p.id === parcelId)
      : parcels.find((p: StatisticsParcel) => p.parcelId === parcelId);
  };

  const getParcelsMetadataByStatisticsSource = (parcelId: string, source: StatisticsSource) => {
    const parcels = statisticsData.value.metadata.parcels as ComparativeStatisticsParcel[];

    return parcels
      ? parcels.filter((p: ComparativeStatisticsParcel) => p.parcelId === parcelId && p.statisticsSource === source)
      : undefined;
  };

  const resetWizard = () => {
    currentStep.value = 1;
    statisticsData.value = {
      name: undefined,
      year: undefined,
      kind: undefined,
      parcels: [],
      metadata: {
        parcels: []
      },
      status: undefined
    };
    clearLayers();
  };

  const loadOffers = async () => {
    const demoOffer = isDemoOfferVisible.value ? await getDemoOffer(statisticsData.value.year) : null;
    const offers = demoOffer ? [demoOffer] : [];

    const response = await parcelApi.getOffers(MODULE_PERMISSION, statisticsData.value.year);
    offers.push(...response.data);

    return offers;
  };

  const loadParcelGroups = async () => {
    const response = await parcelApi.getParcelGroups(MODULE_PERMISSION, statisticsData.value.year);
    return response.data;
  };

  const loadWizardList = async (parcelId: string, source: StatisticsSource, year: number) => {
    const result = await statisticsApi.getStatisticsWizard({ parcelId, source, year });
    return result.data;
  };

  const loadStatisticsById = async (id: string) => {
    const response = await statisticsApi.getStatisticsById(id);
    return response.data;
  };

  const nextStep = async () => {
    if (!isLastStep.value && isCurrentStepValid.value) {
      const nextStepIndex = currentStep.value + 1;
      const nextStep = steps.value[nextStepIndex - 1];

      if (currentStep.value === 2) {
        addParcelsMetadata();
      }
      if (nextStep.onLoad) {
        isNextStepLoading.value = true;
        await nextStep.onLoad();
        isNextStepLoading.value = false;
      }

      currentStep.value = nextStepIndex;
    }
  };

  const previousStep = async () => {
    if (!isFirstStep.value) {
      const prevStepIndex = currentStep.value - 1;
      const prevStep = steps.value[prevStepIndex - 1];

      if (prevStep.onLoad) {
        isNextStepLoading.value = true;
        await prevStep.onLoad();
        isNextStepLoading.value = false;
      }

      currentStep.value = prevStepIndex;
    } else {
      router.push({ name: 'statistics' }).then();
      resetWizard();
    }
  };

  const onStatisticsKindChange = async () => {
    statisticsData.value = {
      ...statisticsData.value,
      kind: statisticsData.value.kind,
      parcels: [],
      metadata: {
        parcels: []
      },
      status: undefined
    };
    statisticsSourceView.value = StatisticsSource.YIELD;
    clearLayers();
  };

  const onStatisticsYearChange = async () => {
    statisticsData.value = {
      ...statisticsData.value,
      year: statisticsData.value.year,
      metadata: {
        parcels: []
      },
      parcels: [],
      status: undefined
    };
    clearLayers();
  };

  const setParcelFormItems = async (parcel: StatisticsWizardParcelData, copiedParcel: boolean) => {
    const { getChild } = useFormElement(parcel.form.element);

    const yieldField = getChild('yield') as SelectableFormElement;
    const zoneStrengthField = getChild('zoneStrength') as SelectableFormElement;
    const appliedDoseField = getChild('appliedDose') as SelectableFormElement;
    const stemNumberField = getChild('stemNumber') as SelectableFormElement;
    const soilSampleField = getChild('soilSample') as SelectableFormElement;

    const { value: yieldValue } = useFormElement(yieldField);
    const { value: zoneStrengthValue } = useFormElement(zoneStrengthField);
    const { value: appliedDoseValue } = useFormElement(appliedDoseField);
    const { value: stemNumberValue } = useFormElement(stemNumberField);
    const { value: soilSampleValue } = useFormElement(soilSampleField);

    switch (statisticsData.value.kind) {
      case StatisticsKind.YIELD_ZONE_STRENGTH:
        await setYieldItems(parcel, copiedParcel);
        await setZoneStrengthItems(parcel, copiedParcel);
        if (copiedParcel) {
          await setupParcelVectorMap(parcel, yieldValue.value, StatisticsSource.YIELD);
          await setupParcelVectorMap(parcel, zoneStrengthValue.value, StatisticsSource.ZONE_STRENGTH);
        }
        break;
      case StatisticsKind.YIELD_APPLIED_DOSE:
        await setYieldItems(parcel, copiedParcel);
        await setAppliedDoseItems(parcel, copiedParcel);
        if (copiedParcel) {
          yieldValue.value && (await setupParcelVectorMap(parcel, yieldValue.value, StatisticsSource.YIELD));
          appliedDoseValue.value &&
            (await setupParcelVectorMapWithMultipleSources(
              parcel,
              appliedDoseValue.value,
              StatisticsSource.APPLIED_DOSE
            ));
        }
        break;
      case StatisticsKind.YIELD_STEM_NUMBER:
        await setYieldItems(parcel, copiedParcel);
        await setStemNumberItems(parcel, copiedParcel);
        if (copiedParcel) {
          await setupParcelVectorMap(parcel, yieldValue.value, StatisticsSource.YIELD);
          await setupParcelVectorMapWithMultipleSources(parcel, stemNumberValue.value, StatisticsSource.STEM_NUMBER);
        }
        break;
      case StatisticsKind.YIELD_SOIL_PARAMETER:
        await setYieldItems(parcel, copiedParcel);
        await setSoilSampleItems(parcel, copiedParcel);
        if (copiedParcel) {
          await setupParcelVectorMap(parcel, yieldValue.value, StatisticsSource.YIELD);
          await setupParcelVectorMap(parcel, soilSampleValue.value, StatisticsSource.SOIL_SAMPLE);
        }
        break;
      default:
        break;
    }
  };

  const setYieldItems = async (parcel: StatisticsWizardParcelData, copiedParcel: boolean) => {
    await setParcelSourceItems('yield', StatisticsSource.YIELD, parcel, copiedParcel);
  };

  const setStemNumberItems = async (parcel: StatisticsWizardParcelData, copiedParcel: boolean) => {
    await setParcelSourceItems('stemNumber', StatisticsSource.STEM_NUMBER, parcel, copiedParcel);
  };

  const setZoneStrengthItems = async (parcel: StatisticsWizardParcelData, copiedParcel: boolean) => {
    await setParcelSourceYearlyItem('zoneStrength', StatisticsSource.ZONE_STRENGTH, parcel, copiedParcel);
  };

  const setAppliedDoseItems = async (parcel: StatisticsWizardParcelData, copiedParcel: boolean) => {
    await setParcelSourceItems('appliedDose', StatisticsSource.APPLIED_DOSE, parcel, copiedParcel);
  };

  const setSoilSampleItems = async (parcel: StatisticsWizardParcelData, copiedParcel: boolean) => {
    await setParcelSourceYearlyItem('soilSample', StatisticsSource.SOIL_SAMPLE, parcel, copiedParcel);
  };

  const setParcelSourceItems = async (
    sourceFieldName: string,
    source: StatisticsSource,
    parcel: StatisticsWizardParcelData,
    copiedParcel: boolean
  ) => {
    try {
      if (statisticsData.value.year) {
        const wizardList = await loadWizardList(parcel.id, source, statisticsData.value.year);
        const wizardListItems = mapToSelectableItems(wizardList, source, false);

        setSelectableFormElementChildItems(sourceFieldName, wizardListItems, parcel.form.element);

        if (copiedParcel) {
          await handleSourceCopy(sourceFieldName, source, parcel);
        } else if (source === StatisticsSource.YIELD) {
          setYieldItem(parcel, sourceFieldName);
        }
      }
    } catch (e) {
      parcel.isLoading = false;
    }
  };

  const setYieldItem = (parcel: StatisticsWizardParcelData, sourceFieldName: string) => {
    const { getChild } = useFormElement(parcel.form.element);
    const child = getChild(sourceFieldName) as SelectableFormElement;
    const filteredItems = child.items.filter(
      (item: ComparativeStatisticsParcel) =>
        item.year === statisticsData.value.year && item.raster && item.layerGroupKind === LayerGroupKind.CELL_YIELD
    );

    if (filteredItems.length > 0) {
      child.value = filteredItems[0];
    }
  };

  const setParcelSourceYearlyItem = async (
    sourceFieldName: string,
    source: StatisticsSource,
    parcel: StatisticsWizardParcelData,
    copiedParcel: boolean
  ) => {
    if (statisticsData.value.year) {
      const yearlyItems: StatisticsWizardListItem[] = await loadWizardList(
        parcel.id,
        source,
        statisticsData.value.year
      );

      const maxYearlyItems = yearlyItems.filter((item) => item.year === Math.max(...yearlyItems.map((i) => i.year)));

      const { getChild, validate } = useFormElement(parcel.form.element);
      const child = getChild(sourceFieldName) as SelectableFormElement;
      if (!child) return;

      if (maxYearlyItems.length > 0) {
        const wizardListItems = mapToSelectableItems(maxYearlyItems, source, true);

        setSelectableFormElementChildItems(sourceFieldName, wizardListItems, parcel.form.element);

        if (!copiedParcel) {
          if ([StatisticsSource.ZONE_STRENGTH, StatisticsSource.SOIL_SAMPLE].includes(source)) {
            child.value = child.items[0];
          }
        } else {
          await handleSourceCopy(sourceFieldName, source, parcel);
        }

        await validate({});
      }
    }
  };

  const mapToSelectableItems = (objectList, source: StatisticsSource, isYearly: boolean) => {
    return objectList.map((item, index) => ({
      name: isYearly
        ? t('statistics.wizard.label.yearlySource', {
            year: item.year,
            layerGroupType: t(`statistics.wizard.label.${item.layerGroupType}`)
          })
        : item.name,
      value: index,
      layerGroupId: item.id,
      layerGroupKind: item.layerGroupType,
      yieldAmount: item.yieldAmount,
      statisticsSource: source,
      year: item.year,
      raster: item.raster
    }));
  };

  const handleSourceCopy = async (
    sourceFieldName: string,
    source: StatisticsSource,
    parcel: StatisticsWizardParcelData
  ) => {
    if (!statisticsData.value.kind || !statisticsData.value.parcels) return;

    try {
      const parcelsMetadata = await getParcelsMetadataByStatisticsSource(parcel.id, source);

      if (!parcelsMetadata) return;

      const { getChild, validate } = useFormElement(parcel.form.element);
      const sourceField = getChild(sourceFieldName) as SelectableFormElement;
      const { value: sourceFieldValue } = useFormElement(sourceField);

      const matchingItems = sourceField.items.filter((item) => {
        return parcelsMetadata.some(
          (metadata) => item.layerGroupId === metadata.layerGroupId && item.layerGroupKind === metadata.layerGroupKind
        );
      });

      sourceFieldValue.value = fieldHasMultipleValues(source) ? matchingItems : matchingItems[0];
      await validate();
    } catch (e) {
      console.error(e);
    }
  };

  const openParcelSelectorModal = async () => {
    try {
      isParcelsLoading.value = true;

      const offers = await loadOffers();
      const parcelGroups = await loadParcelGroups();

      modals.open<ParcelGroupModalConfig>(ParcelGroupModal, {
        title: 'statistics.modal.openParcelsTitle',
        confirmBtnLabel: 'button.open',
        text: '',
        width: '1000px',
        contentHeight: '500px',
        persistent: true,
        scrollable: true,
        translate: true,
        hideGroupForm: true,
        offers: offers,
        parcelGroups: parcelGroups,
        blockWithoutValidZoning: statisticsData.value.kind !== StatisticsKind.SOIL_TEMPERATURE,
        onConfirm: async (parcels) => {
          await addSelectedParcels(parcels);
        },
        onCancel: () => {
          isParcelsLoading.value = false;
        }
      });
    } catch {
      notifications.showError('statistics.wizard.messages.loadOffersError');
      isParcelsLoading.value = false;
    }
  };

  const addSelectedParcels = async (parcels: StatisticsWizardParcelItem[]) => {
    const year = statisticsData.value.year;

    try {
      if (year) {
        for (const parcel of parcels) {
          await setupSelectedParcel(parcel, false);
        }

        fitParcelsExtent();
        isParcelsLoading.value = false;
      }
    } catch (e) {
      notifications.showError('statistics.wizard.messages.openParcelsError');
      isParcelsLoading.value = false;
    }
  };

  const setupSelectedParcel = async (parcel: StatisticsWizardParcelItem, copiedParcel: boolean) => {
    const alreadyAdded = !!statisticsData.value.parcels.find((p) => p.id === parcel.id);
    if (alreadyAdded) return;

    const statisticsWizardParcel: StatisticsWizardParcelData = {
      id: parcel.id,
      name: parcel.name,
      area: parcel.area || 0,
      svg: parcel.svg,
      mapData: null,
      rasterLayerGroup: undefined,
      vectorLayerGroup: undefined,
      selectedLayerGroup: undefined,
      zones: [],
      blocked: parcel.blocked,
      state: StatisticsWizardParcelDataState.VALID,
      bbox: transformExtent(parcel.bbox ?? [], MAP_PROJECTION_4326, MAP_PROJECTION_3857) as [
        number,
        number,
        number,
        number
      ],
      isLoading: true,
      formIsLoading: false,
      form: createForm(getStatisticsWizardParcelFormConfig(statisticsData.value.kind)),
      copied: copiedParcel,
      currentCrop: parcel.currentCrop
    };

    if (statisticsData.value.kind === StatisticsKind.SOIL_TEMPERATURE) {
      await setupSelectedSoilTemperatureParcel(parcel);
    }

    statisticsData.value.parcels.push(statisticsWizardParcel);
    validateParcel(parcel.id);
  };

  const setupSelectedSoilTemperatureParcel = async (parcel: StatisticsWizardParcelItem) => {
    try {
      const wizard = await getStatisticsPreview(
        parcel.id,
        undefined,
        LayerGroupKind.SOIL_TEMPERATURE,
        statisticsData.value.year
      );

      const features: any[] = wizard.features;
      const mainFeatureData = features.shift();

      const mapLayerGroup = createVectorMapLayerGroup({
        id: parcel.id,
        name: parcel.name,
        mapLayerConfigs: [
          {
            id: mainFeatureData.id,
            classification: '',
            type: wizard.type,
            name: '',
            featureProjection: MAP_PROJECTION_3857,
            dataProjection: MAP_PROJECTION_4326,
            rawData: mainFeatureData
          }
        ]
      });

      addLayer(mapLayerGroup);
    } catch (e) {
      console.error(e);
      const errorParcel = statisticsData.value.parcels.find((p) => p.id === parcel.id);
      if (errorParcel) {
        errorParcel.isLoading = false;
      }
    }
  };

  const loadRasterFile = async (groupId: string) => {
    const response = await parcelApi.getRasterFile(groupId);
    return response.data;
  };

  const getStatisticsPreviewFile = async (layerGroupId: string) => {
    const response = await statisticsApi.getStatisticsPreviewFile({ layerGroupId });
    return response.data;
  };

  const setupParcelVectorMap = async (
    parcel: StatisticsWizardParcelData,
    sourceData: ComparativeStatisticsParcel,
    statisticsSource: StatisticsSource
  ) => {
    try {
      removePreviousLayerIfNeeded(parcel, statisticsSource);

      if (!sourceData) {
        return;
      }

      parcel.formIsLoading = true;

      if (sourceData.layerGroupKind && sourceData.layerGroupId) {
        if (sourceData.raster) {
          await buildGeoTiffLayer(parcel, sourceData);
        } else {
          const previewData = await getStatisticsPreview(
            parcel.id,
            [sourceData.layerGroupId],
            sourceData.layerGroupKind,
            sourceData.year
          );

          await buildVectorLayer(parcel, previewData, sourceData.year, sourceData.statisticsSource);
        }
      }

      addBuildedLayerIfNeeded(parcel, sourceData.statisticsSource);
      parcel.formIsLoading = false;
    } catch (e) {
      console.error(e);
      parcel.isLoading = false;
      parcel.formIsLoading = false;
    }
  };

  const setupParcelVectorMapWithMultipleSources = async (
    parcel: StatisticsWizardParcelData,
    sourceData: ComparativeStatisticsParcel[],
    statisticsSource: StatisticsSource
  ) => {
    removePreviousLayerIfNeeded(parcel, statisticsSource);

    if (!sourceData.length || !sourceData[0].layerGroupKind || !sourceData.some((source) => source.layerGroupId))
      return;

    parcel.formIsLoading = true;

    const layerStatSource = sourceData[0].statisticsSource;

    const layerGroupIds = sourceData.map((source) => source.layerGroupId!);

    try {
      const previewData = await getStatisticsPreview(
        parcel.id,
        layerGroupIds,
        sourceData[0].layerGroupKind,
        sourceData[0].year
      );

      buildVectorLayer(parcel, previewData, sourceData[0].year, sourceData[0].statisticsSource);

      addBuildedLayerIfNeeded(parcel, layerStatSource);

      parcel.formIsLoading = false;
    } catch (e) {
      //TODO: error notification?
      console.error(e);
      parcel.isLoading = false;
      parcel.formIsLoading = false;
    }
  };

  const previewSourceHasMultipleLayersWithSameLayerType = (statisticsSource: StatisticsSource) => {
    return [StatisticsSource.APPLIED_DOSE, StatisticsSource.STEM_NUMBER].includes(statisticsSource);
  };

  const buildVectorLayer = async (
    parcel: StatisticsWizardParcelData,
    previewData: any,
    year: number,
    statisticsSource: StatisticsSource
  ) => {
    if (!previewData) return;

    const features = previewData.features || [];
    if (!features.length) return;

    const layersData = features[0].properties.layers;
    if (!layersData || !layersData.length) return;

    const mappedSubLayersData = layersData.map((layerData, index) => ({
      id: `${parcel.id}-preview-${index}`,
      type: previewData.type,
      kind: layerData.kind,
      colorMap: layerData.colorMap,
      unitType: layerData.unitType,
      name: layerData.name,
      features: features.map((feature) => {
        const layerProperties = feature.properties.layers.find((l: any) => l.name === layerData.name);
        return { ...feature, properties: { value: layerProperties?.value, colorMap: layerProperties?.colorMap } };
      })
    }));

    const mapLayerConfigs: VectorMapLayerConfig[] = mappedSubLayersData.map((subLayerData, index) => {
      return {
        id: subLayerData.id,
        colorMap: colorMapStore.findColorMap(subLayerData.colorMap || '') as any,
        unitType: subLayerData.unitType,
        classification: LayerGroupClassification.ZONE,
        type: previewSourceHasMultipleLayersWithSameLayerType(statisticsSource) ? subLayerData.name : subLayerData.kind,
        name: subLayerData.name,
        featureProjection: MAP_PROJECTION_3857,
        dataProjection: MAP_PROJECTION_4326,
        rawData: subLayerData,
        enabled: index === 0,
        zoneLabelFn: integerValueUnitZoneLabelFn,
        zoneStyleFn: defaultZoneStyleFn,
        mapInfo: {
          enabled: index === 0,
          subtitleFn: parcelYearMapInfoSubTitleFn,
          valueFn: defaultStatisticsMapInfoValueFn
        }
      } as VectorMapLayerConfig;
    });

    const layerGroup = createVectorMapLayerGroup({
      id: parcel.id,
      name: parcel.name,
      mapLayerConfigs: mapLayerConfigs,
      customData: {
        parcelName: parcel.name,
        year: year,
        source: statisticsSource,
        features: previewData.features.map((feature: any) => feature.properties)
      }
    });

    parcel.vectorLayerGroup = layerGroup;
  };

  const buildGeoTiffLayer = async (parcel: StatisticsWizardParcelData, sourceData: ComparativeStatisticsParcel) => {
    if (!sourceData.layerGroupId) return;

    const previewFile = await getStatisticsPreviewFile(sourceData.layerGroupId);

    const subLayersData = previewFile.bands || [];
    if (!subLayersData.length) return;

    const blob = await loadRasterFile(sourceData.layerGroupId);
    const image = await getGeoTiffImage(blob);
    const noDataValue = 0;
    const classification = previewFile.layerGroupType.classification;

    const mainSubLayerMasterData = subLayersData.shift() as LayerGroupBand;

    const mapLayerConfigs: GeoTiffMapLayerConfig[] = [
      await createGeoTiffLayerConfig(
        mainSubLayerMasterData,
        blob,
        image,
        noDataValue,
        sourceData.statisticsSource,
        classification
      )
    ];

    if (subLayersData.length > 0) {
      for (const subLayerMasterData of subLayersData) {
        mapLayerConfigs.push(
          await createGeoTiffLayerConfig(
            subLayerMasterData,
            blob,
            image,
            noDataValue,
            sourceData.statisticsSource,
            classification
          )
        );
      }
    }

    const layerGroup = createGeoTiffMapLayerGroup({
      id: parcel.id,
      name: parcel.name,
      mapLayerConfigs: mapLayerConfigs,
      customData: {
        parcelName: parcel.name,
        year: mainSubLayerMasterData.year,
        source: sourceData.statisticsSource
      }
    });

    parcel.rasterLayerGroup = layerGroup;
  };

  const createGeoTiffLayerConfig = (
    subLayerMasterData: LayerGroupBand,
    blob: any,
    image: GeoTIFFImage,
    noDataValue: number,
    statisticsSource: StatisticsSource,
    classification: string
  ) => {
    const subLayerColorMap = colorMapStore.findColorMap(subLayerMasterData.colorMap || '');
    const subLayerType = subLayerColorMap?.id || '';

    return {
      id: subLayerMasterData.id,
      classification,
      type: subLayerType,
      name: subLayerMasterData.name,
      band: subLayerMasterData.index,
      normalize: false,
      interpolate: false,
      unitType: subLayerMasterData.unitType || undefined,
      unitTypeName: subLayerMasterData.unitTypeName || undefined,
      rawData: {
        blob,
        image,
        noDataValue
      },
      customData: {
        source: statisticsSource
      },
      featureProjection: MAP_PROJECTION_3857,
      dataProjection: MAP_PROJECTION_4326,
      colorMap: subLayerColorMap as any,
      enabled: true,
      mapInfo: {
        enabled: true,
        subtitleFn: parcelYearMapInfoSubTitleFn
      },
      opacity: 1
    };
  };

  const removePreviousLayerIfNeeded = (parcel: StatisticsWizardParcelData, source: StatisticsSource) => {
    if (parcel.rasterLayerGroup && isSourceActiveInPreview(source) && source === StatisticsSource.YIELD) {
      removeLayer(parcel.rasterLayerGroup.id);
      parcel.selectedLayerGroup = undefined;
      parcel.rasterLayerGroup = undefined;
    }

    if (parcel.vectorLayerGroup && isSourceActiveInPreview(source) && source !== StatisticsSource.YIELD) {
      removeLayer(parcel.vectorLayerGroup.id);
      parcel.selectedLayerGroup = undefined;
      parcel.vectorLayerGroup = undefined;
    }
  };

  const addBuildedLayerIfNeeded = (parcel: StatisticsWizardParcelData, source: StatisticsSource) => {
    if (parcel.rasterLayerGroup && isSourceActiveInPreview(source) && source === StatisticsSource.YIELD) {
      addLayer(parcel.rasterLayerGroup);
      parcel.selectedLayerGroup = parcel.rasterLayerGroup;
    }

    if (parcel.vectorLayerGroup && isSourceActiveInPreview(source) && source !== StatisticsSource.YIELD) {
      addLayer(parcel.vectorLayerGroup);
      parcel.selectedLayerGroup = parcel.vectorLayerGroup;
    }
  };

  const isSourceActiveInPreview = (source: StatisticsSource) => {
    return statisticsSourceView.value && statisticsSourceView.value === source;
  };

  const getStatisticsPreview = async (
    parcelId: string,
    layerGroupIds: string[] | undefined,
    layerGroupKind: LayerGroupKind,
    year?: number
  ) => {
    const result = await statisticsApi.getStatisticsPreview({
      parcelId,
      layerGroupId: layerGroupIds,
      layerGroupKind,
      year
    });
    return result.data;
  };

  const fitParcelsExtent = () => {
    const extent = statisticsData.value.parcels.reduce((prev: Extent, cur) => mergeExtents(prev, cur.bbox), []);
    zoomToExtent(extent);
  };

  const removeParcel = (parcelItem: StatisticsWizardParcelData) => {
    const index = statisticsData.value.parcels.findIndex((p) => p.id === parcelItem.id);

    if (index >= 0) {
      statisticsData.value.parcels.splice(index, 1);
      removeLayer(parcelItem.id);
      fitParcelsExtent();
    }
    updateMapInfos();
  };

  const validateParcel = (id: string) => {
    const parcel = statisticsData.value.parcels.find((p: StatisticsWizardParcelData) => p.id === id);

    if (!parcel) return;

    if (parcel.blocked) {
      parcel.state = StatisticsWizardParcelDataState.INVALID_INPUT;
      parcel.valid = false;
    }
  };

  const saveStatistics = async () => {
    if (!isStatisticsSaveValid.value) return;

    isSaving.value = true;

    let parcelsToSave: StatisticsParcel[] = [];

    if (statisticsData.value.kind === StatisticsKind.SOIL_TEMPERATURE) {
      parcelsToSave = statisticsData.value.parcels.map(
        (parcelData) =>
          ({
            parcelId: parcelData.id,
            layerGroupKind: LayerGroupKind.SOIL_TEMPERATURE
          } as SoilTemperatureStatisticsParcel)
      );
      if (statisticsData.value.metadata.parcels.length) {
        statisticsData.value.metadata.parcels = (
          statisticsData.value.metadata.parcels as SoilTemperatureStatisticsParcelMetadata[]
        ).map(
          (parcel: SoilTemperatureStatisticsParcelMetadata) =>
            ({
              id: parcel.parcelId,
              dateFrom: parcel.dateFrom,
              dateTo: parcel.dateTo
            } as SoilTemperatureStatisticsParcelMetadata)
        );
      }
    } else if (statisticsData.value.metadata.parcels.length) {
      parcelsToSave = (statisticsData.value.metadata.parcels as ComparativeStatisticsParcelMetadata[]).map(
        (parcel: ComparativeStatisticsParcelMetadata) =>
          ({
            parcelId: parcel.parcelId,
            layerGroupId: parcel.layerGroupId,
            layerGroupKind: parcel.layerGroupKind,
            value: parcel.value,
            statisticsSource: parcel.statisticsSource,
            raster: parcel.raster
          } as ComparativeStatisticsParcel)
      );
    }

    if (!statisticsData.value.kind || !statisticsData.value.year || !statisticsData.value.name) {
      return;
    }

    const statistics: Statistics = {
      kind: statisticsData.value.kind,
      name: statisticsData.value.name,
      year: statisticsData.value.year,
      metadata: statisticsData.value.metadata,
      parcels: parcelsToSave
    };

    try {
      await statisticsApi.createStatistics(statistics);

      notifications.showSuccess('statistics.wizard.messages.saveStatisticsSuccess');
      isSaving.value = false;

      resetWizard();
      await router.push({ name: 'statistics' });
    } catch {
      notifications.showError('statistics.wizard.messages.saveStatisticsError');
      isSaving.value = false;
    }
  };

  const validateStatisticsSave = () => {
    isStatisticsSaveValid.value =
      !!statisticsData.value.kind &&
      !!statisticsData.value.year &&
      !!statisticsData.value.name &&
      statisticsData.value.parcels.length > 0;
  };

  const validateSourceStep = () => allParcelsValid.value;

  const onSourceStepLoad = async () => {
    await nextTick(() => {
      openSourceInfoModal(`maximap.statistics.${statisticsData.value.kind}.wizard.info`);
      fitParcelsExtent();
    });
  };

  watch(statisticsSourceView, () => {
    addLayersOfCurrentSource();
  });

  const addLayersOfCurrentSource = () => {
    clearLayers();

    parcelsLoading.value = true;
    setTimeout(() => {
      addLayLerByCurrentSource();
    }, 300);
  };

  const addLayLerByCurrentSource = async () => {
    for (const parcel of statisticsData.value.parcels) {
      parcel.selectedLayerGroup = undefined;
      if (parcel.vectorLayerGroup && parcel.vectorLayerGroup.customData.source === statisticsSourceView.value) {
        parcel.selectedLayerGroup = parcel.vectorLayerGroup;
        addLayer(parcel.vectorLayerGroup);
      }

      if (parcel.rasterLayerGroup && parcel.rasterLayerGroup.customData.source === statisticsSourceView.value) {
        parcel.selectedLayerGroup = parcel.rasterLayerGroup;
        addLayer(parcel.rasterLayerGroup);
      }
    }
    parcelsLoading.value = false;
  };

  return {
    map,
    isZoneScrollIntoViewDisabled,
    steps,
    currentStep,
    isFirstStep,
    isLastStep,
    isCurrentStepValid,
    isNextStepLoading,
    isParcelsLoading,
    showStatisticsTypeChooser,
    isUiDisabled,
    isSaving,
    statisticsData,
    isStatisticsSaveValid,
    needMetadataUpdate,
    statisticsSourceView,
    parcelsLoading,
    updateMapInfos,
    openInfoModal,
    setParcelFormItems,
    applySoilTemperaturePeriod,
    getParcelsMetadataByStatisticsSource,
    getParcelMetadataByParcelId,
    copyStatistics,
    fitParcelsExtent,
    saveStatistics,
    setupParcelVectorMap,
    setupParcelVectorMapWithMultipleSources,
    getStatisticsPreview,
    addLayer,
    loadWizardList,
    nextStep,
    previousStep,
    openParcelSelectorModal,
    resetWizard,
    removeParcel,
    validateParcel,
    validateStatisticsSave,
    onStatisticsKindChange,
    onStatisticsYearChange,
    zoomToExtent,
    switchSubLayer
  };
});
